import { useMutation } from "@apollo/client";
import { useAlerts } from "components/Alerts/AlertProvider";
import { extensionFromName } from "components/Document/shared";
import { DocumentType } from "generated-graphql/graphql";
import { useUploadFiles } from "hooks/useDocumentUpload";
import { useUpsertDocuments } from "hooks/useUpsertDocuments";
import { DialogType, useMinimizedDialogs } from "providers/minimizedDialogs";
import { useCallback, useRef } from "react";

import { EXTRACT_BILL_OF_LADING } from "./api";

/**
 * Shown when reading the document does not start — the mutation rejected, or it
 * came back without a job id. The document is uploaded and created by that
 * point, so staying silent leaves the dialog open with the file still selected
 * and the reviewer no reason not to submit it a second time. Submitting again
 * is what the message invites, and it reuses the document this attempt already
 * created rather than making a second one.
 */
const READING_NOT_STARTED =
  "The bill of lading uploaded, but reading it did not start. The document is saved, so there is no need to upload it again. Submit again to read it.";

/** Puts the shared analyzing dialog on screen for one extraction job. */
function useAnalyzingDialogLauncher({
  onClose,
  onExtractionReady,
}: {
  onClose: () => void;
  onExtractionReady?: (documentIds: string[], facilityId: string) => void;
}) {
  const alerts = useAlerts();
  const { createDialog } = useMinimizedDialogs();

  return useCallback(
    (
      jobId: string,
      appliedFacilityId: string,
      uploadedDocumentId: string,
      onDiscardRequested: () => void
    ) => {
      const dialogId = `bill-of-lading-analyzing-${jobId}`;
      createDialog({
        id: dialogId,
        dialogType: DialogType.DocumentAIAnalyzing,
        props: {
          open: true,
          jobId,
          preferOriginalTitle: true,
          dialogId,
          // Opted in so a failed reading still reaches the review dialog, which
          // holds the only retry control. Without it the analyzing dialog's
          // review button stays disabled on failure, leaving the reviewer an
          // uploaded document and no way to read it again.
          reviewOnFailure: true,
          onReview: (documentIds: string[]) => {
            // Falls back to the document this submit created: a run that fails
            // early enough reports no documents of its own, and the review
            // dialog still needs one to open and retry.
            onExtractionReady?.(
              documentIds.length > 0 ? documentIds : [uploadedDocumentId],
              appliedFacilityId
            );
          },
          onDiscard: onDiscardRequested,
          discardLabel: "Discard upload",
          onCloseWithoutReview: () => {
            alerts.info(
              "Bill of lading saved. The document data won't be available until it's been reviewed."
            );
          },
        },
        onDismiss: onClose,
      });
    },
    [createDialog, onClose, onExtractionReady, alerts]
  );
}

/**
 * Upload the file, create the `WasteShippingDocument`-typed document, queue
 * `extractBillOfLading`, and launch the shared analyzing dialog on success.
 * Split out of `useBillOfLadingUpload` to keep each hook under the
 * complexity lint's line-count limit.
 */
export function useBillOfLadingSubmit({
  tenantId,
  onClose,
  onExtractionReady,
  onUploaded,
}: {
  tenantId: string;
  onClose: () => void;
  onExtractionReady?: (documentIds: string[], facilityId: string) => void;
  /** Records the created document id so discard can find it. */
  onUploaded: (documentId: string) => void;
}) {
  const alerts = useAlerts();
  const uploadFiles = useUploadFiles();
  const { upsertDocuments } = useUpsertDocuments();
  const [extractBillOfLading] = useMutation(EXTRACT_BILL_OF_LADING);
  const launchAnalyzingDialog = useAnalyzingDialogLauncher({ onClose, onExtractionReady });

  // The document this hook already uploaded and created, against the file and
  // facility it was created for. A submit that gets past creation and then fails
  // to start the reading leaves the dialog open on the same file; resubmitting
  // reuses this document instead of uploading a second copy and orphaning the
  // first. A different file is a different document, and so is a different
  // facility — the association is written at creation, so a reviewer who
  // switches the picker after a failure needs a new one.
  const alreadyCreated = useRef<{ file: File; facilityId: string; documentId: string } | null>(
    null
  );

  const createDocument = useCallback(
    async (file: File, facilityId: string) => {
      const reusable = alreadyCreated.current;
      if (reusable?.file === file && reusable.facilityId === facilityId) return reusable.documentId;

      const [storageLink] = await uploadFiles([{ file, documentData: { tenantId } }]);
      if (!storageLink) {
        alerts.error("The file failed to upload.");
        return null;
      }

      const created = await upsertDocuments({
        documents: [
          {
            tenantId,
            title: file.name,
            fileExtension: extensionFromName(file.name),
            storageLink,
            documentType: DocumentType.WasteShippingDocument,
            isArchived: false,
          },
        ],
        associations: [{ tenantId, facilityId }],
        currentSubmissionDocument: false,
      });

      const documentId = created?.[0]?.id;
      if (!documentId) {
        alerts.error("The document could not be created.");
        return null;
      }
      alreadyCreated.current = { file, facilityId, documentId };
      onUploaded(documentId);
      return documentId;
    },
    [tenantId, uploadFiles, upsertDocuments, onUploaded, alerts]
  );

  const submit = useCallback(
    async ({
      file,
      facilityId,
      onDiscardRequested,
    }: {
      file: File;
      facilityId: string;
      onDiscardRequested: () => void;
    }) => {
      const documentId = await createDocument(file, facilityId);
      if (!documentId) return false;

      // Caught rather than thrown on: the dialog's own catch reports that the
      // upload failed, which by here is not what happened — the document is
      // saved, and only the reading did not start.
      let jobId: string | null | undefined;
      try {
        const { data } = await extractBillOfLading({ variables: { documentId } });
        jobId = data?.extractBillOfLading;
      } catch (err) {
        alerts.error(READING_NOT_STARTED, err instanceof Error ? err : undefined);
        return false;
      }

      if (!jobId) {
        alerts.error(READING_NOT_STARTED);
        return false;
      }
      launchAnalyzingDialog(jobId, facilityId, documentId, onDiscardRequested);
      return true;
    },
    [createDocument, extractBillOfLading, launchAnalyzingDialog, alerts]
  );

  return { submit };
}
